import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, GlobalStyles, Stack, Typography } from '@mui/material';
import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { IVerbaScholaLesson, IVerbaScholaCourse, IVerbaScholaLessonPlanSection, IVerbaScholaLessonGeneratedMaterial, IVerbaScholaLessonVocabularyTarget, IVerbaScholaLessonGrammarTarget, VerbaScholaApi } from '../api/VerbaScholaApi';
import { BORDER, INTER, NAVY, POPPINS, SLATE, BLUE } from '../style/verbaScholaStyles';
import { instructionalPinyin } from '../component/studentInstructionalPinyin';
import LessonText from '../component/LessonText';

type PrintablePlan = {
    lesson: IVerbaScholaLesson;
    course: IVerbaScholaCourse;
    sections: IVerbaScholaLessonPlanSection[];
    materials: IVerbaScholaLessonGeneratedMaterial[];
    vocabulary: IVerbaScholaLessonVocabularyTarget[];
    grammar: IVerbaScholaLessonGrammarTarget[];
};

const sectionOrder = ['context', 'outcomes', 'language', 'materials', 'preparation', 'support', 'procedure', 'assessment'];
const sectionNames: Record<string, string> = {
    context: 'Class context', outcomes: 'Learning outcomes', language: 'Language targets',
    preparation: 'Preparation', support: 'Challenges & support', procedure: 'Classroom procedure', assessment: 'Assessment & follow-up',
};

async function readPrintablePlan(courseId: number, lessonId: number): Promise<PrintablePlan> {
    for (let attempt = 0; attempt < 3; attempt++) {
        const before = (await VerbaScholaApi.getLesson(lessonId)).data;
        if (before.verbaschola_course_id !== courseId) throw new Error('lesson_course_mismatch');
        const [course, sections, materials, vocabulary, grammar] = await Promise.all([
            VerbaScholaApi.getCourse(courseId), VerbaScholaApi.getLessonPlanSectionList(lessonId),
            VerbaScholaApi.getLessonGeneratedMaterialList(lessonId), VerbaScholaApi.getLessonVocabularyTargetList(lessonId),
            VerbaScholaApi.getLessonGrammarTargetList(lessonId),
        ]);
        const lesson = (await VerbaScholaApi.getLesson(lessonId)).data;
        if (lesson.authoring_revision === before.authoring_revision) {
            return { lesson, course: course.data, sections: sections.data, materials: materials.data, vocabulary: vocabulary.data, grammar: grammar.data };
        }
    }
    throw new Error('workspace_changed_during_load');
}

/** `?include=plan`, `?include=handouts` or both (the default) choose which pages the PDF contains. */
function readIncluded(search: string) {
    const include = new URLSearchParams(search).get('include');
    const parts = include ? include.split(',') : ['plan', 'handouts'];
    return { plan: parts.includes('plan'), handouts: parts.includes('handouts') };
}

// The print stylesheet sets the @page margin to zero so the browser has no room for its own
// date, title and URL header and footer. These sizes put the white space back inside the page.
const PRINT_MARGIN_X = '16mm';
const PRINT_MARGIN_Y = '14mm';

/**
 * One PDF sheet: the teacher plan or a single handout. A table's header and footer rows repeat
 * on every printed page, so these blank rows keep the top and bottom margins when a long plan
 * runs onto more pages.
 */
function PdfSheet({ label, children }: { label: string; children: React.ReactNode }) {
    const spacer = <tr><td className="lesson-pdf-spacer" /></tr>;
    return <Box component="section" aria-label={label} className="lesson-pdf-page" sx={{
        '@media screen': { border: `1px solid ${BORDER}`, borderRadius: '8px', boxShadow: '0 1px 4px rgba(15, 23, 42, 0.08)', p: { xs: 3, sm: 6 }, mb: 4 },
        '@media print': { px: PRINT_MARGIN_X },
    }}>
        <Box component="table" role="presentation" sx={{ width: '100%', borderCollapse: 'collapse', '& td': { p: 0 },
            '& .lesson-pdf-spacer': { height: 0, '@media print': { height: PRINT_MARGIN_Y } } }}>
            <thead>{spacer}</thead>
            <tfoot>{spacer}</tfoot>
            <tbody><tr><td>{children}</td></tr></tbody>
        </Box>
    </Box>;
}

const languageHeadingSx = { fontWeight: 700, fontSize: 14, mt: 1.5 };
const languageRowSx = { display: 'grid', gap: 1.5, alignItems: 'baseline', py: 0.75, borderBottom: `1px solid ${BORDER}`, fontSize: 13, breakInside: 'avoid' };

function LanguageTargets({ vocabulary, grammar }: { vocabulary: IVerbaScholaLessonVocabularyTarget[]; grammar: IVerbaScholaLessonGrammarTarget[] }) {
    const ordered = <T extends { id: number; order_index?: number | null }>(rows: T[]) => [...rows].sort((a, b) => (a.order_index ?? a.id) - (b.order_index ?? b.id));
    return <>
        {vocabulary.length > 0 && <>
            <Typography component="h3" sx={languageHeadingSx}>Vocabulary</Typography>
            <Box component="ul" aria-label="Vocabulary" sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.5 }}>
                {ordered(vocabulary).map(row => <Box component="li" key={row.id} sx={{ ...languageRowSx, gridTemplateColumns: 'minmax(4.5em, max-content) minmax(6em, max-content) 1fr' }}>
                    <Typography component="span" lang="zh" sx={{ fontSize: 16, fontWeight: 600 }}>{row.hanzi}</Typography>
                    <Typography component="span" sx={{ fontSize: 13, color: SLATE }}>{instructionalPinyin(row.pinyin)}</Typography>
                    <Typography component="span" sx={{ fontSize: 13 }}>{row.meaning}{row.is_review && <Box component="span" sx={{ color: SLATE }}> (review)</Box>}</Typography>
                </Box>)}
            </Box>
        </>}
        {grammar.length > 0 && <>
            <Typography component="h3" sx={languageHeadingSx}>Grammar</Typography>
            <Box component="ul" aria-label="Grammar" sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.5 }}>
                {ordered(grammar).map(row => <Box component="li" key={row.id} sx={{ ...languageRowSx, gridTemplateColumns: '1fr', gap: 0.25 }}>
                    <Typography component="span" lang="zh" sx={{ fontSize: 14, fontWeight: 600 }}>{row.pattern}</Typography>
                    {row.meaning && <Typography component="span" sx={{ fontSize: 13 }}>{row.meaning}</Typography>}
                    {row.example && <LessonText component="span" lang="zh" sx={{ fontSize: 13, color: SLATE, whiteSpace: 'pre-wrap' }}>Example: {row.example}</LessonText>}
                </Box>)}
            </Box>
        </>}
    </>;
}

function StudentHandout({ material, plan, index, total }: { material: IVerbaScholaLessonGeneratedMaterial; plan: PrintablePlan; index: number; total: number }) {
    const label = `Student Handout ${index + 1} of ${total}`;
    return <PdfSheet label={`${label}: ${material.title}`}>
        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ color: SLATE, fontSize: 12, borderBottom: `1px solid ${BORDER}`, pb: 1 }}>
            <Typography component="span" sx={{ fontSize: 12 }}>{plan.course.name} · {plan.lesson.title}</Typography>
            <Typography component="span" sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{label}</Typography>
        </Stack>
        <Stack direction="row" spacing={3} sx={{ mt: 2.5, fontSize: 14 }}>
            {['Name', 'Date'].map(field => <Stack key={field} direction="row" alignItems="flex-end" spacing={1} sx={{ flex: field === 'Name' ? 2 : 1 }}>
                <Typography sx={{ fontSize: 14 }}>{field}:</Typography>
                <Box sx={{ flex: 1, borderBottom: `1px solid ${NAVY}`, mb: '4px' }} />
            </Stack>)}
        </Stack>
        <Typography component="h2" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 22, mt: 3 }}>{material.title}</Typography>
        {material.material_format && <Typography sx={{ color: SLATE, fontSize: 13, mt: 0.25 }}>{material.material_format}</Typography>}
        <LessonText sx={{ whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.8, mt: 2.5 }}>{material.body_text}</LessonText>
    </PdfSheet>;
}

/** A separate route lets the browser save the plan and handouts as a PDF without clipping a builder dialog. */
export default function VerbaScholaTeacherPrintablePlanPage() {
    const { courseId, lessonId } = useParams() as { courseId: string; lessonId: string };
    const history = useHistory();
    const location = useLocation();
    const [plan, setPlan] = React.useState<PrintablePlan | null>(null);
    const [error, setError] = React.useState(false);
    const [included, setIncluded] = React.useState(() => readIncluded(location.search));
    React.useEffect(() => {
        let cancelled = false;
        readPrintablePlan(Number(courseId), Number(lessonId)).then(value => { if (!cancelled) setPlan(value); })
            .catch(() => { if (!cancelled) setError(true); });
        return () => { cancelled = true; };
    }, [courseId, lessonId]);

    const search = new URLSearchParams(location.search);
    const from = search.get('from') === 'exercises' ? 'exercises' : 'plan';
    const close = () => {
        window.close();
        // A directly opened print URL cannot close itself in most browsers.
        window.setTimeout(() => { if (!window.closed) history.replace(`/teacher/course/${courseId}/lesson/${lessonId}/${from}`); }, 150);
    };
    const toggle = (part: 'plan' | 'handouts') => {
        const next = { ...included, [part]: !included[part] };
        setIncluded(next);
        search.set('include', (['plan', 'handouts'] as const).filter(key => next[key]).join(','));
        history.replace({ search: search.toString() });
    };

    // The document title becomes the browser's suggested PDF file name.
    React.useEffect(() => {
        if (!plan) return undefined;
        const previous = document.title;
        const kind = included.plan && included.handouts ? 'Lesson plan and handouts' : included.handouts ? 'Student handouts' : 'Lesson plan';
        document.title = `${plan.lesson.title} - ${kind}`;
        return () => { document.title = previous; };
    }, [plan, included]);

    const sections = plan ? [...plan.sections].sort((a, b) =>
        (sectionOrder.indexOf(a.section_type ?? '') === -1 ? sectionOrder.length : sectionOrder.indexOf(a.section_type ?? '')) -
        (sectionOrder.indexOf(b.section_type ?? '') === -1 ? sectionOrder.length : sectionOrder.indexOf(b.section_type ?? '')) ||
        (a.order_index ?? a.id) - (b.order_index ?? b.id)) : [];
    const sectionType = (section: IVerbaScholaLessonPlanSection) => sectionOrder.includes(section.section_type ?? '')
        ? section.section_type : 'procedure';
    const sectionGroups = sectionOrder.filter(group => sections.some(section => sectionType(section) === group)
        || group === 'language' && Boolean(plan && (plan.vocabulary.length || plan.grammar.length))
        || group === 'materials' && Boolean(plan?.materials.length));
    const materials = plan ? [...plan.materials].sort((a, b) => (a.order_index ?? a.id) - (b.order_index ?? b.id)) : [];
    const handouts = materials.filter(material => material.body_text?.trim());
    const handoutNumber = (material: IVerbaScholaLessonGeneratedMaterial) => handouts.indexOf(material) + 1;
    const nothingSelected = !included.plan && !included.handouts;

    return <Box sx={{ minHeight: '100vh', bgcolor: '#fff', color: NAVY, fontFamily: INTER }}>
        <GlobalStyles styles={{ '@media print': {
            // A zero page margin leaves the browser nowhere to print its date/title header and URL footer.
            '@page': { size: 'auto', margin: 0 },
            'html, body, #root': { width: 'auto', minHeight: 0, height: 'auto', overflow: 'visible !important', background: '#fff' },
            '.teacher-print-controls': { display: 'none !important' },
            '.teacher-print-item': { breakInside: 'avoid' },
            // Every plan and handout starts on a fresh sheet so handouts can be printed and copied on their own.
            '.lesson-pdf-page + .lesson-pdf-page': { breakBefore: 'page' },
            'h2, h3': { breakAfter: 'avoid' },
        } }} />
        <Stack className="teacher-print-controls" direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}
            sx={{ p: 2, borderBottom: `1px solid ${BORDER}` }}>
            <Button startIcon={<ArrowBackRoundedIcon />} onClick={close} sx={{ color: BLUE, textTransform: 'none', minHeight: 44, alignSelf: 'flex-start' }}>Close PDF view</Button>
            <Stack direction="row" alignItems="center" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <FormControlLabel control={<Checkbox checked={included.plan} onChange={() => toggle('plan')} />} label="Teacher plan" />
                <FormControlLabel control={<Checkbox checked={included.handouts} onChange={() => toggle('handouts')} />} label={`Student handouts${plan ? ` (${handouts.length})` : ''}`} />
                <Button variant="contained" startIcon={<PictureAsPdfRoundedIcon />} disabled={!plan || nothingSelected} onClick={() => window.print()} sx={{ minHeight: 44 }}>Save as PDF</Button>
            </Stack>
        </Stack>
        {plan && <Typography className="teacher-print-controls" sx={{ color: SLATE, fontSize: 13, textAlign: 'center', mt: 2, px: 2 }}>
            In the print dialog, choose <strong>Save as PDF</strong> as the destination. Each student handout prints on its own page.
        </Typography>}
        {error && <Alert severity="error" sx={{ m: 3 }}>The saved lesson plan could not be loaded. Return to the lesson and try again.</Alert>}
        {!error && !plan && <Stack alignItems="center" sx={{ p: 8 }}><CircularProgress aria-label="Loading printable lesson plan" /></Stack>}
        {plan && <Box component="main" sx={{ maxWidth: 850, mx: 'auto', p: { xs: 2, sm: 4 }, '@media print': { maxWidth: 'none', p: 0 } }}>
            {nothingSelected && <Alert severity="info">Choose the teacher plan, the student handouts or both to build the PDF.</Alert>}
            {included.plan && <PdfSheet label="Teacher plan">
            <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 28 }}>{plan.lesson.title}</Typography>
            <Typography sx={{ color: SLATE, fontSize: 14, mt: 0.5 }}>In-class lesson plan · {plan.course.name}{plan.lesson.duration_minutes ? ` · ${plan.lesson.duration_minutes} minutes` : ''}{plan.lesson.proficiency_level ? ` · ${plan.lesson.proficiency_level.replace(/^ACTFL_/, '').replace(/_/g, ' ')}` : ''}{plan.lesson.character_set ? ` · ${plan.lesson.character_set.replace(/_/g, ' ')}` : ''}</Typography>
            {sections.length === 0 && plan.materials.length === 0 && plan.vocabulary.length === 0 && plan.grammar.length === 0 && <Alert severity="info" sx={{ mt: 3 }}>No saved plan sections, language targets or materials yet. Add them in the Lesson Plan editor, then reopen the PDF export.</Alert>}
            {sectionGroups.map(group => <React.Fragment key={group}><Box sx={{ mt: 3 }}>
                <Typography component="h2" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, borderBottom: `1px solid ${BORDER}`, pb: 0.5 }}>{sectionNames[group] ?? 'Teaching materials'}</Typography>
                {sections.filter(section => sectionType(section) === group).map((section, index) => <Box key={section.id} className="teacher-print-item" sx={{ mt: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Typography component="h3" sx={{ fontWeight: 700, fontSize: 14 }}>{group === 'procedure' ? `${index + 1}. ` : ''}{section.title}</Typography>
                        {section.duration_minutes != null && <Typography sx={{ color: SLATE, whiteSpace: 'nowrap', fontSize: 13 }}>{section.duration_minutes} min</Typography>}
                    </Stack>
                    {section.body && <LessonText sx={{ whiteSpace: 'pre-wrap', color: SLATE, fontSize: 13, lineHeight: 1.6, mt: 0.4 }}>{section.body}</LessonText>}
                </Box>)}
                {group === 'language' && <LanguageTargets vocabulary={plan.vocabulary} grammar={plan.grammar} />}
            </Box>
            {group === 'materials' && materials.length > 0 && <Box sx={{ mt: 1 }}>
                {materials.map(material => <Box key={material.id} className="teacher-print-item" sx={{ mt: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{material.title}{material.material_format ? ` · ${material.material_format}` : ''}</Typography>
                    {/* With handouts included, each material prints in full on its own page instead of being repeated here. */}
                    {included.handouts && handoutNumber(material) > 0
                        ? <Typography sx={{ color: SLATE, fontSize: 13 }}>See Student Handout {handoutNumber(material)}.</Typography>
                        : material.body_text && <LessonText sx={{ whiteSpace: 'pre-wrap', color: SLATE, fontSize: 13, lineHeight: 1.6 }}>{material.body_text}</LessonText>}
                </Box>)}
            </Box>}
            </React.Fragment>)}
            </PdfSheet>}
            {included.handouts && handouts.length === 0 && <Alert severity="info" className="teacher-print-controls" sx={{ mt: 2 }}>This lesson has no student handouts with text yet. Add materials in the Lesson Plan editor to include them.</Alert>}
            {included.handouts && handouts.map((material, index) => <StudentHandout key={material.id} material={material} plan={plan} index={index} total={handouts.length} />)}
        </Box>}
    </Box>;
}
